import { Component, HostBinding, HostListener, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SideBarComponent } from 'src/app/components/side-bar/side-bar.component';
import { SvgAtlasComponent } from '@bioreferencehealth/ui-kit';
import { LogUserInteraction } from 'src/modules/keep-alive/decorators/keep-alive-user-interaction.decorator';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Store } from '@ngxs/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, map, of, startWith, switchMap } from 'rxjs';
import {
  defaultSidebarWidthRem,
  maxSidebarWidthRem,
  minSidebarWidthRem,
  remToPx,
  sidebarPinnedMinViewportWidthRem
} from 'src/app/components/side-bar/consts/sidebar-width.const';
import { SidebarPreferencesState } from 'src/app/components/side-bar/sidebar-preferences.state';
import { SetSidebarPinned, SetSidebarWidth } from 'src/app/components/side-bar/sidebar-preferences.actions';

@Component({
  standalone: true,
  templateUrl: './authenticated-user-layout.component.html',
  imports: [RouterOutlet, SvgAtlasComponent, SideBarComponent, CdkScrollable],
  styleUrls: ['./authenticated-user-layout.component.scss']
})
export class AuthenticatedUserLayoutComponent {
  private readonly store = inject(Store);

  // Reactively derived from NGXS state; falls back to default when no preference is stored.
  public readonly sidebarWidth = toSignal(this.store.select(SidebarPreferencesState.widthRem).pipe(map(w => w ?? defaultSidebarWidthRem)), {
    initialValue: defaultSidebarWidthRem
  });

  // Reactively derived: uses the stored preference when set, otherwise tracks the viewport width.
  public readonly isSidebarPinned = toSignal(
    this.store.select(SidebarPreferencesState.isPinned).pipe(
      switchMap(storedPinned => {
        if (storedPinned !== null) {
          return of(storedPinned);
        }
        return fromEvent(window, 'resize').pipe(
          startWith(null),
          map(() => window.innerWidth >= remToPx(sidebarPinnedMinViewportWidthRem))
        );
      })
    ),
    { initialValue: true }
  );

  public isSidebarAutoCollapsed = true;
  public isSidebarResizable = false;

  @HostBinding('style.--sidebar-width')
  protected get sidebarWidthCss(): string {
    const effectiveSidebarWidth = this.isSidebarPinned() ? this.sidebarWidth() : minSidebarWidthRem;
    return `${effectiveSidebarWidth}rem`;
  }

  public onSidebarWidthChange(width: number): void {
    this.store.dispatch(new SetSidebarWidth(this.clampSidebarWidth(width)));
  }

  public onSidebarAutoCollapsedChange(isAutoCollapsed: boolean): void {
    this.isSidebarAutoCollapsed = isAutoCollapsed;
  }

  public onSidebarPinnedChange(isPinned: boolean): void {
    if (isPinned) {
      this.isSidebarAutoCollapsed = false;
    }
    this.store.dispatch(new SetSidebarPinned(isPinned));
  }

  @HostListener('document:click')
  @HostListener('document:keydown')
  @LogUserInteraction()
  protected onUserInteraction(): void {}

  private clampSidebarWidth(width: number): number {
    if (Number.isNaN(width)) {
      return defaultSidebarWidthRem;
    }

    return Math.max(minSidebarWidthRem, Math.min(maxSidebarWidthRem, width));
  }
}
